import { useState } from 'react';
import { XMarkIcon, BookmarkIcon } from '@heroicons/react/24/outline';
import { scopeService } from '../../services/scopeService';
import type { Deliverable } from '@shared/types';
import toast from 'react-hot-toast';

interface SaveScopeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  deliverables: Deliverable[];
  onSaved?: () => void;
}

export function SaveScopeDialog({
  isOpen,
  onClose,
  deliverables,
  onSaved,
}: SaveScopeDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a scope name');
      return;
    }

    if (!deliverables || deliverables.length === 0) {
      toast.error('No deliverables to save');
      return;
    }

    setSaving(true);
    const toastId = toast.loading('Saving scope...');

    try {
      await scopeService.createScope({
        name: name.trim(),
        description: description.trim() || undefined,
        deliverables,
      });

      toast.success('Scope saved successfully!', { id: toastId });
      
      // Reset form
      setName('');
      setDescription('');
      
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error('Failed to save scope:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save scope';
      toast.error(errorMessage, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setName('');
      setDescription('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
          onClick={handleClose}
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-md">
                <BookmarkIcon className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Save Scope
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={saving}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            <p className="text-sm text-gray-600 mb-4">
              Save the current deliverables as a reusable scope that you can load into future contracts.
            </p>

            <div className="space-y-4">
              {/* Scope Name */}
              <div>
                <label htmlFor="scope-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Scope Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="scope-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., SharePoint Intranet Implementation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={saving}
                />
              </div>

              {/* Description (optional) */}
              <div>
                <label htmlFor="scope-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <textarea
                  id="scope-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Brief description of this scope..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={saving}
                />
              </div>

              {/* Deliverables count */}
              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{deliverables.length}</span> deliverable
                  {deliverables.length !== 1 ? 's' : ''} will be saved
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim() || !deliverables.length}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BookmarkIcon className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Scope'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

